import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { BookmarkCheck, Loader2, Trash2 } from "lucide-react";
import { CompanyLogo } from "./ManageCompanies";
import { EmployerInfoButton } from "@/components/EmployerInfo";

function formatSaved(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

export default function SavedCompanies() {
  const [items, setItems] = useState(null);
  const [removingId, setRemovingId] = useState(null);

  const fetchSaved = useCallback(async () => {
    try {
      const { data } = await api.get("/companies/saved");
      setItems(data.items);
    } catch {
      toast.error("Could not load saved companies.");
      setItems([]);
    }
  }, []);

  useEffect(() => { fetchSaved(); }, [fetchSaved]);

  const remove = async (company) => {
    const ok = window.confirm(
      `Remove ${company.client_name} from saved companies?\n\nHeads up: the company credit used to save it is NOT refunded. Saving it again later counts as a new company credit.`
    );
    if (!ok) return;
    setRemovingId(company.id);
    try {
      await api.delete(`/companies/${company.id}/save`);
      setItems((list) => list.filter((c) => c.id !== company.id));
      toast.success(`${company.client_name} removed from saved.`);
    } catch {
      toast.error("Could not remove company.");
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <div data-testid="saved-companies-page" className="space-y-5">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight font-display">Saved Companies</h1>
        <p className="text-slate-500 mt-1">Companies you're tracking from our network.</p>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        {items === null ? (
          <div className="py-16 text-center"><Loader2 className="w-7 h-7 mx-auto animate-spin text-amber-500" /></div>
        ) : items.length === 0 ? (
          <div className="py-16 text-center text-slate-500">
            <BookmarkCheck className="w-10 h-10 mx-auto mb-3 text-slate-300" />
            <p className="font-medium text-slate-700">No saved companies yet</p>
            <p className="text-sm mt-1">Save companies from the directory to track them here.</p>
            <Link to="/app/companies">
              <Button data-testid="browse-companies-button"
                className="mt-5 bg-amber-500 text-black hover:bg-amber-400 font-semibold transition-colors">
                Browse Companies
              </Button>
            </Link>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {items.map((c) => (
              <li key={c.id} data-testid={`saved-company-row-${c.id}`}
                className="flex items-center gap-4 px-5 py-4 hover:bg-slate-50/60 transition-colors">
                <CompanyLogo company={c} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-slate-900">{c.client_name}</span>
                    <EmployerInfoButton company={c.client_name} testid={`saved-company-info-${c.id}`} />
                    {c.new_openings && (
                      <span className="px-2 py-0.5 rounded-md bg-amber-100 text-amber-700 text-[11px] font-semibold">New openings</span>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5 truncate">
                    {[c.industry, c.location].filter(Boolean).join(" · ") || "—"}
                    {c.contact_name ? ` · Contact: ${c.contact_name}` : ""}
                  </p>
                </div>
                <span className="hidden sm:block text-xs text-slate-400 whitespace-nowrap">Saved {formatSaved(c.saved_at)}</span>
                <button
                  data-testid={`saved-company-remove-${c.id}`}
                  onClick={() => remove(c)}
                  disabled={removingId === c.id}
                  className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg border border-rose-200 text-rose-600 text-sm font-medium hover:bg-rose-50 transition-colors disabled:opacity-50">
                  <Trash2 className="w-3.5 h-3.5" /> Remove
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
